import { useState, useEffect, useContext } from 'react';
import ModalBtn from '../../molecules/modalButton/view';
import Form from 'react-bootstrap/Form';
import { Col, Row } from 'react-bootstrap';
import { PlusCircle } from 'lucide-react';
import ButtonLoading from '../../atoms/buttonLoading/view';
import { dispatchCustomEvent } from '../../../utils/dispatch';
import { getReceiptTypes } from '../../../usecases/receipts-usecase';
import { roleContext } from '../../../context/role/context';
import printJS from 'print-js';

// Importar estrategias
import GeneralReceiptStrategy from './strategies/GeneralReceiptStrategy';
import ReceivablePaymentStrategy from './strategies/ReceivablePaymentStrategy';

/**
 * Componente principal para crear diferentes tipos de comprobantes.
 * Utiliza el patrón Strategy para manejar diferentes tipos de comprobantes.
 * 
 * Tipos soportados:
 * - Recibo de Caja General
 * - Abono a Cuenta por Cobrar
 */
function CreateReceipt() {
    const { profile } = useContext(roleContext);

    // Estado para el tipo de comprobante seleccionado
    const [receiptType, setReceiptType] = useState('general'); // 'general' o 'receivable'

    // Estado para la estrategia actual
    const [strategy, setStrategy] = useState(new GeneralReceiptStrategy());

    // Estado para los datos del formulario
    const [formData, setFormData] = useState(strategy.getInitialState());

    // Estado para errores de validación
    const [errors, setErrors] = useState({});

    // Estado para tipos de recibo (necesario para GeneralReceiptStrategy)
    const [receiptTypes, setReceiptTypes] = useState([]);

    useEffect(() => {
        fetchReceiptTypes();
    }, []);

    /**
     * Carga los tipos de recibo desde el backend.
     */
    async function fetchReceiptTypes() {
        try {
            const result = await getReceiptTypes(1, 100);
            setReceiptTypes(result.data || []);
        } catch (err) {
            console.error("Error fetching receipt types:", err);
            dispatchCustomEvent("popup", {
                message: "Error al cargar tipos de recibo",
                type: "error"
            });
        }
    }

    /**
     * Maneja la apertura del modal.
     * Refresca los datos necesarios.
     */
    function handleOpen() {
        fetchReceiptTypes();
    }

    /**
     * Maneja el cambio de tipo de comprobante.
     * Cambia la estrategia y resetea el formulario.
     * 
     * @param {string} type - Tipo de comprobante ('general' o 'receivable')
     */
    function handleReceiptTypeChange(type) {
        setReceiptType(type);

        let newStrategy;
        if (type === 'general') {
            newStrategy = new GeneralReceiptStrategy();
        } else if (type === 'receivable') {
            newStrategy = new ReceivablePaymentStrategy();
        }

        setStrategy(newStrategy);
        setFormData(newStrategy.getInitialState());
        setErrors({});
    }

    /**
     * Maneja el envío del formulario.
     * Valida los datos usando la estrategia actual y los envía al backend.
     */
    async function handleSubmit() {
        // Validar usando la estrategia actual
        const validation = strategy.validate(formData);

        if (!validation.isValid) {
            setErrors(validation.errors);

            // Mostrar mensaje de error
            let errorMessage = "Por favor, completa todos los campos requeridos.";
            if (validation.errors.payment_methods) {
                errorMessage = validation.errors.payment_methods;
            } else if (validation.errors.amount) {
                errorMessage = validation.errors.amount;
            }

            dispatchCustomEvent("popup", {
                message: errorMessage,
                type: "error"
            });
            dispatchCustomEvent("loading-btn", {});
            return;
        }

        try {
            // Preparar contexto para la estrategia
            const context = {
                uid: profile.uid,
                cid: profile.cid,
                profile: profile,
                receiptTypes: receiptTypes
            };

            // Enviar usando la estrategia actual
            const result = await strategy.submit(formData, context);

            // Mostrar mensaje de éxito
            dispatchCustomEvent("popup", {
                message: receiptType === 'general' ? "Recibo creado exitosamente" : "Abono registrado exitosamente"
            });

            // Cerrar modal
            dispatchCustomEvent("close-modal", {});

            // Disparar evento de actualización
            dispatchCustomEvent(strategy.getUpdateEventName(), {});

            // Imprimir comprobante
            printReceipt(result);

            // Resetear formulario
            setFormData(strategy.getInitialState());
            setErrors({});

        } catch (err) {
            console.error("Error creating receipt:", err);

            let errorMessage = "Error al crear comprobante";
            if (err.status === 429) {
                errorMessage = "Alcanzaste el límite de operaciones";
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            }

            dispatchCustomEvent("popup", {
                message: errorMessage,
                type: "error"
            });
            dispatchCustomEvent("loading-btn", {});
        }
    }

    /**
     * Imprime el comprobante usando la estrategia actual.
     * 
     * @param {Object} data - Datos del comprobante a imprimir
     */
    function printReceipt(data) {
        const htmlContent = strategy.generatePrintContent(data, profile);

        // Crear o actualizar contenedor de impresión
        let container = document.getElementById('print-container');
        if (!container) {
            container = document.createElement('div');
            container.id = 'print-container';
            container.style.display = 'none';
            document.body.appendChild(container);
        }

        container.innerHTML = htmlContent;

        // Imprimir
        printJS({
            printable: 'print-content',
            type: 'html',
            targetStyles: ['*'],
        });
    }

    // Obtener el componente de formulario de la estrategia actual
    const FormComponent = strategy.getFormComponent();

    return (
        <ModalBtn
            id={'create-receipt-modal'}
            title="Crear Comprobante"
            icon={<PlusCircle size={20} />}
            onClick={handleOpen}
        >
            <Form
                action="javascript:void(0);"
                id="receipt-form"
            >
                {/* Selector de tipo de comprobante */}
                <Form.Group as={Col} controlId="receipt-type-selector" className="mb-3">
                    <Form.Label>Tipo de Comprobante</Form.Label>
                    <Form.Select
                        value={receiptType}
                        onChange={(e) => handleReceiptTypeChange(e.target.value)}
                    >
                        <option value="general">Recibo de Caja General</option>
                        <option value="receivable">Abono a Cuenta por Cobrar</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                        {receiptType === 'general'
                            ? 'Registra ingresos o egresos generales de caja'
                            : 'Registra abonos a cuentas pendientes de clientes'
                        }
                    </Form.Text>
                </Form.Group>

                <hr />

                {/* Formulario dinámico según la estrategia */}
                <FormComponent
                    data={formData}
                    errors={errors}
                    onChange={setFormData}
                />

            </Form>
            {/* Botón de envío */}
            <ButtonLoading
                onClick={handleSubmit}
                id="create-receipt-btn"
                title={strategy.getSubmitButtonTitle()}
                className="btn-pill btn-pill-primary w-100 mt-4"
            />
        </ModalBtn>
    );
}

export default CreateReceipt;
